import { SimpleSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { trace } from "@opentelemetry/api";
import { checkTokenExpiryAndRefresh } from "./LoginUtils";
import packageJson from "../../package.json";

const OTEL_SERVICE_URL = process.env.REACT_APP_OTEL_SERVICE_URL;
const appVersion = packageJson.version;

export const loadOpenTelemetry = () => {
  // headers is a function so the token is re-read (and refreshed if expired)
  // on every span export, instead of being baked in once at startup.
  const exporter = new OTLPTraceExporter({
    url: OTEL_SERVICE_URL,
    headers: async () => {
      await checkTokenExpiryAndRefresh();
      return { Authorization: localStorage.getItem("auth") };
    },
  });

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: "aft-admin-client",
    }),
    spanProcessors: [new SimpleSpanProcessor(exporter)],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
  });

  let previousElement;

  const userInteractionInstrumentation = new UserInteractionInstrumentation({
    eventNames: ["click"],
    shouldPreventSpanCreation: (_event, element, span) => {

      //Dedup by skipping repeat spans      
      if (previousElement && previousElement.isEqualNode(element)) {
        return true;
      }
      previousElement = element;
      setTimeout(() => {
        previousElement = null;
      }, 0);

      if (span) {
        const userId = localStorage.getItem("loggedInUserId");

        span.setAttribute("userId", userId || "");
        span.setAttribute("target_element", element.tagName);

        const closestElementWithId = element.id
          ? element
          : element.parentElement
            ? element.parentElement
            : element;

        if (closestElementWithId?.id) {
          span.setAttribute("target_xpath", closestElementWithId.id);          
        }
        if (element.textContent?.trim()) {
          span.setAttribute("target_xpath_extra", element.textContent.trim().slice(0, 120));
        }
        if (element.dataset?.value) {
          span.setAttribute("value", element.dataset.value);
        }
        span.setAttribute(
          "http.url",
          window.location.hash ? window.location.hash.slice(1) : window.location.pathname
        );
        span.setAttribute("app-version", appVersion);

        return false;
      }

      return true;
    },
  });

  const fetchInstrumentation = new FetchInstrumentation({
    propagateTraceHeaderCorsUrls: [new RegExp(process.env.REACT_APP_API_ENDPOINT)],
    applyCustomAttributesOnSpan: (span) => {
      const userId = localStorage.getItem("loggedInUserId");
      if (userId) {
        span.setAttribute("userId", userId);
      }
      span.setAttribute("app-version", appVersion);
    },
  });

  registerInstrumentations({
    instrumentations: [fetchInstrumentation, userInteractionInstrumentation],
  });
};

export const tracer = trace.getTracer("aft-admin-client");
